import type { DailyRoutineDetails } from '../dto/DailyRoutineDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { DailyRoutineRepository } from '../ports/DailyRoutineRepository';
import type { DailyStepRepository } from '../ports/DailyStepRepository';
import type { EmotionalProtocolLogRepository } from '../ports/EmotionalProtocolLogRepository';
import type { RoutinePersistenceTransactions } from '../ports/RoutinePersistenceTransactions';
import type { RoutineStepTemplateRepository } from '../ports/RoutineStepTemplateRepository';
import { reorderDailySteps } from '../../domain/routine/ReorderDailySteps';
import type { RoutineStepTemplate } from '../../domain/routine/models';
import type { DailyStepId, ProfileId, RoutineStepTemplateId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';
import { loadRoutineDetails } from './routineDetails';

export type ReorderScope = 'today' | 'today_and_future';

export class ReorderDailySteps {
  public constructor(
    private readonly dailyRoutines: DailyRoutineRepository,
    private readonly dailySteps: DailyStepRepository,
    private readonly routineStepTemplates: RoutineStepTemplateRepository,
    private readonly transactions: RoutinePersistenceTransactions,
    private readonly clock: Clock,
    private readonly emotionalProtocolLogs: EmotionalProtocolLogRepository,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
    orderedIds: readonly DailyStepId[],
    scope: ReorderScope,
  ): Promise<Result<DailyRoutineDetails, RoutineError>> {
    try {
      const routine = await this.dailyRoutines.findByLogicalDate(profileId, logicalDate);
      if (!routine) return failure(new RoutineError('routine_not_found', 'Journée introuvable.'));
      if (routine.profileId !== profileId) {
        return failure(
          new RoutineError('reorder_forbidden', 'Cette journée ne peut pas être modifiée.'),
        );
      }
      const timestamp = this.clock.now().toISOString();
      const currentSteps = await this.dailySteps.listByRoutine(routine.id);
      const reordered = reorderDailySteps(currentSteps, orderedIds, timestamp);
      let templates: readonly RoutineStepTemplate[] | undefined;

      if (scope === 'today_and_future' && routine.templateId) {
        const currentTemplates = await this.routineStepTemplates.listByTemplate(routine.templateId);
        const orderedTemplateIds = reordered.flatMap((step) =>
          step.templateStepId ? [step.templateStepId] : [],
        );
        if (new Set(orderedTemplateIds).size !== orderedTemplateIds.length) {
          return failure(
            new RoutineError('reorder_duplicate_template', 'Ordre permanent invalide.'),
          );
        }
        const matched = new Set<RoutineStepTemplateId>(orderedTemplateIds);
        const orderedTemplates = orderedTemplateIds.map((id) =>
          currentTemplates.find((template) => template.id === id),
        );
        if (orderedTemplates.some((template) => !template)) {
          return failure(
            new RoutineError('reorder_unknown_template', 'Modèle de mission introuvable.'),
          );
        }
        let matchedIndex = 0;
        templates = [...currentTemplates]
          .sort((left, right) => left.order - right.order)
          .map((template, order) => {
            const selected = matched.has(template.id) ? orderedTemplates[matchedIndex++] : template;
            return { ...selected!, order, updatedAt: timestamp };
          });
      }

      await this.transactions.reorderSteps(routine, reordered, templates);
      return success(
        await loadRoutineDetails(routine, this.dailySteps, this.emotionalProtocolLogs),
      );
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
